import { sameUnitSignature, type UnitSignature } from '@agavi/blocks';
import { unit as mathUnit, createUnit, Unit } from 'mathjs';

// ─── Custom unit extensions ───────────────────────────────────────────────────
// Registered once at module load against mathjs's global instance.
// try/catch handles the case where a unit already exists natively in a future
// mathjs version, or HMR re-evaluates this module.
//
// Entries with prefixes: 'short' automatically support SI prefixes —
// e.g. defining 'pc' gives 'kpc', 'Mpc', 'Gpc' for free.

export const CUSTOM_UNITS: Array<{
  name: string;
  definition: string;
  description: string;
  category: string;
  /** SI prefix table. 'short' gives k/M/G/m/µ; 'long' gives kilo/centi/milli, which is what the
   *  spelled-out international forms need — `centimetre` is `centi` + `metre`. */
  prefixes?: 'short' | 'long';
  /** Further spellings registered against the same definition, e.g. the plural. */
  aliases?: string[];
}> = [
  // ── International (Commonwealth) spellings ────────────────────
  // mathjs is American-only: it knows `meter` and `liter` but not `metre` or `gramme`, and it is
  // case-strict, so a student in the UK, India, Australia or anywhere else writing the spelling
  // they were taught was simply marked wrong. `litre` and `tonne` are already native; these two
  // families are the real gap.
  //
  // `prefixes: 'long'` is what carries the family: it yields centimetre, kilometre, millimetre and
  // the rest, which is the whole point — registering the bare word would fix `metre` and leave
  // `centimetre` broken, which is a worse place to stop than not starting.
  { name: 'metre',  definition: '1 meter', description: 'Metre (international spelling)', category: 'Length',
    prefixes: 'long', aliases: ['metres'] },
  { name: 'gramme', definition: '1 gram',  description: 'Gramme (international spelling)', category: 'Mass',
    prefixes: 'long', aliases: ['grammes'] },

  // ── Common velocity aliases ──────────────────────────────────────────────
  { name: 'mph',    definition: '1 mi/h',               description: 'Miles per hour',           category: 'velocity'   },
  { name: 'kph',    definition: '1 km/h',               description: 'Kilometres per hour',      category: 'velocity'   },

  // ── Mechanics ────────────────────────────────────────────────────────────
  // Revolutions are dimensionless, so rpm = 1/min — converts cleanly to Hz.
  // rad/s requires a 2π factor (physics choice, not a unit conversion).
  // `1/60 s^-1` does not parse — mathjs rejects a leading fraction in a definition, so this unit
  // silently failed to register for as long as it has existed. The decimal is String(1/60), which
  // round-trips to the identical double, so nothing is lost by not writing the fraction.
  { name: 'rpm',    definition: '0.016666666666666666 s^-1', description: 'Revolutions per minute', category: 'Mechanics' },

  // ── Optics ───────────────────────────────────────────────────────────────
  { name: 'diopter', definition: '1 m^-1',              description: 'Diopter (lens power)',     category: 'optics'     },

  // ── Dimensionless ratios ────────────────────
  // Stable-isotope work reports delta values in per mille, and there was previously no way to
  // express that at all. mathjs treats `%` as an operator rather than a unit, and has none of
  // these. Registered smallest-last so the list reads by magnitude.
  { name: 'percent',  definition: '0.01',  description: 'Percent',      category: 'Ratio',
    aliases: ['pct'] },
  { name: 'permille', definition: '0.001', description: 'Per mille',    category: 'Ratio',
    aliases: ['permil'] },
  { name: 'ppm',      definition: '1e-6',  description: 'Parts per million', category: 'Ratio' },
  { name: 'ppb',      definition: '1e-9',  description: 'Parts per billion', category: 'Ratio' },

  // ── SI units mathjs simply lacks ────────────────────
  // `Pa` exists but the spelled-out `pascal` does not, which is inconsistent with `newton` and
  // `joule`, which do. The rest are missing in BOTH forms — a teacher measuring light with a lux
  // meter, or working with radiation dose, had no unit to name at all.
  //
  // ORDER MATTERS: the loop registers in array order, and `lm` is defined in terms of `sr`, `lx` in
  // terms of `lm`. Out of order they throw and the catch hides it, which is exactly how `Da` and
  // `Ci` stayed broken.
  { name: 'pascal', definition: '1 Pa',      description: 'Pascal (pressure)',        category: 'Mechanics',
    prefixes: 'long', aliases: ['pascals'] },
  { name: 'sr',     definition: '1',         description: 'Steradian (solid angle)',  category: 'Geometry',
    aliases: ['steradian', 'steradians'] },
  { name: 'lm',     definition: '1 cd*sr',   description: 'Lumen (luminous flux)',    category: 'Optics',
    prefixes: 'short', aliases: ['lumen', 'lumens'] },
  { name: 'lx',     definition: '1 lm/m^2',  description: 'Lux (illuminance)',        category: 'Optics',
    prefixes: 'short', aliases: ['lux'] },
  { name: 'Gy',     definition: '1 J/kg',    description: 'Gray (absorbed dose)',     category: 'Chemistry',
    prefixes: 'short', aliases: ['gray', 'grays'] },
  { name: 'Sv',     definition: '1 J/kg',    description: 'Sievert (dose equivalent)', category: 'Chemistry',
    prefixes: 'short', aliases: ['sievert', 'sieverts'] },

  // ── Bases that other entries below depend on ────────────────────
  // Both were referenced by a definition without ever being defined, so `Da` and `Ci` threw on
  // registration and were swallowed by the loop's catch. Declared here, ahead of their dependants,
  // because the loop registers in array order.
  { name: 'u',      definition: '1.66053906660e-27 kg', description: 'Unified atomic mass unit',   category: 'Chemistry' },
  { name: 'Bq',     definition: '1 s^-1',               description: 'Becquerel (radioactivity)',  category: 'Chemistry',
    prefixes: 'short', aliases: ['becquerel', 'becquerels'] },

  // ── Chemistry ────────────────────────────────────────────────────────────
  // Da supports prefixes: kDa and MDa are standard in biochemistry
  { name: 'Da',     definition: '1 u',                  description: 'Dalton (molecular mass)',  category: 'chemistry'  },
  { name: 'molar',  definition: '1 mol/L',              description: 'Molar (mol/L)',            category: 'chemistry',
    prefixes: 'long', aliases: ['molars'] },
  // The symbol, which is how concentration is written on every bottle in the room, and which
  // carries the prefixes chemistry uses: mM, µM, nM. The `molar` entry above claimed those in a
  // comment and registered none of them — a prefix table it never had.
  { name: 'M',      definition: '1 molar',              description: 'Molar (symbol)',           category: 'chemistry',
    prefixes: 'short' },
  // Curie — supports prefixes: mCi, μCi, nCi common in nuclear medicine
  { name: 'Ci',     definition: '3.7e10 Bq',            description: 'Curie (radioactivity)',    category: 'chemistry'  },

  // ── Machining ────────────────────
  // A thousandth of an inch. mathjs has the American name (`mil`) and not the British one, which is
  // the only name it has anywhere engineering drawings are read in imperial outside the US.
  { name: 'thou',   definition: '1 mil',                 description: 'Thou (thousandth of an inch)', category: 'Length' },

  // ── Kitchen measures ────────────────────
  // mathjs has the words and none of the abbreviations, which are how anyone actually writes them.
  // Defined at exactly 1x the word, so the equivalence test finds them from either direction.
  { name: 'tsp',    definition: '1 teaspoon',            description: 'Teaspoon',                 category: 'Volume',
    aliases: ['tspn', 'tsps'] },
  { name: 'tbsp',   definition: '1 tablespoon',          description: 'Tablespoon',               category: 'Volume',
    aliases: ['tbs', 'tbsps'] },

  // ── Astronomy — distance ─────────────────────────────────────────────────
  // mathjs has NEITHER the abbreviation nor the word — the comment here used to claim it had
  // `lightyear`, and it does not, which left the symbol as the only way to write either of these.
  // Both are spelled out far more often than they are abbreviated. Supports kly/Mly, kpc/Mpc/Gpc.
  { name: 'ly',     definition: '9.4607304725808e15 m',  description: 'Light-year',              category: 'astronomy',
    prefixes: 'short', aliases: ['lightyear', 'lightyears'] },
  { name: 'pc',     definition: '3.085677581e16 m',      description: 'Parsec',                  category: 'astronomy',
    prefixes: 'short', aliases: ['parsec', 'parsecs'] },
  // Geological and astronomical time. `a` (annum) already prefixes to ka/Ma/Ga, which is how
  // geology writes it; astronomy writes kyr/Myr/Gyr, and `yr` did not exist at all. Not `y`, which
  // would prefix to `Gy` and collide with the gray.
  { name: 'yr',     definition: '1 year',                description: 'Year',                    category: 'astronomy',
    prefixes: 'short', aliases: ['yrs'] },

  // ── Astronomy — stellar properties ──────────────────────────────────────
  { name: 'Msun',   definition: '1.9885e30 kg',          description: 'Solar mass',              category: 'astronomy'  },
  { name: 'Rsun',   definition: '6.957e8 m',             description: 'Solar radius',            category: 'astronomy'  },
  { name: 'Lsun',   definition: '3.828e26 W',            description: 'Solar luminosity',        category: 'astronomy'  },

  // ── Astronomy — planetary ────────────────────────────────────────────────
  { name: 'Mearth', definition: '5.972e24 kg',           description: 'Earth mass',              category: 'astronomy'  },
  { name: 'Rearth', definition: '6.371e6 m',             description: 'Earth radius',            category: 'astronomy'  },
  { name: 'Mjup',   definition: '1.898e27 kg',           description: 'Jupiter mass',            category: 'astronomy'  },
  { name: 'Rjup',   definition: '7.149e7 m',             description: 'Jupiter radius',          category: 'astronomy'  },

  // ── Astronomy — flux density ─────────────────────────────────────────────
  // 1 Jy = 10⁻²⁶ W/(m²·Hz) = 10⁻²⁶ kg/s²; supports mJy, μJy in radio astronomy
  { name: 'Jy',     definition: '1e-26 kg s^-2',         description: 'Jansky (flux density)',   category: 'astronomy'  },


  // ── Nuclear / atomic physics ─────────────────────────────────────────────
  // mathjs already has eV, u (amu), Bq, Gy, Sv
  { name: 'barn',   definition: '1e-28 m^2',             description: 'Barn (nuclear cross-section)', category: 'nuclear' },

  // ── Geology ──────────────────────────────────────────────────────────────
  // Defined directly in seconds (not aliased to mathjs's built-in 'year') since
  // aliasing a non-prefixable built-in unit doesn't propagate prefix support.
  // Supports ka, Ma, Ga — the standard geologic/astronomic age abbreviations.
  { name: 'a',      definition: '31557600 s',            description: 'Annum (year) — supports ka, Ma, Ga', category: 'geology' },
];

// Units that support SI prefix scaling (k, M, G, m, μ, n, etc.)
const _prefixed = new Set(['ly', 'pc', 'Da', 'molar', 'Ci', 'Jy', 'a']);

for (const { name, definition, prefixes, aliases } of CUSTOM_UNITS) {
  try {
    if (prefixes || aliases) {
      // The per-entry form. Definition nested in the options object for the same reason as below.
      createUnit(name, {
        definition,
        ...(prefixes ? { prefixes } : {}),
        ...(aliases ? { aliases } : {}),
      });
    } else if (_prefixed.has(name)) {
      // Definition must be nested inside the options object — createUnit(name, definition,
      // { prefixes }) (definition as a separate positional arg) silently fails to apply
      // prefixes in mathjs 15.2.0, verified empirically. This form is the one that works.
      createUnit(name, { definition, prefixes: 'short' });
    } else {
      createUnit(name, definition);
    }
  } catch {
    // Already defined — skip silently
  }
}

// ─── Capitalised temperature names ───────────────────────
// Celsius, Fahrenheit, Kelvin and Rankine are people, and "degrees Celsius" is capitalised in
// ordinary writing — the one documented exception to SI's rule that unit NAMES are lowercase even
// when derived from a name. mathjs is case-strict and has only the lowercase forms, so a teacher or
// student writing the spelling they were taught was simply wrong.
//
// Deliberately limited to temperature. Case IS load-bearing for SYMBOLS — K vs k is kelvin against
// kilo, N vs n is newton against nano — so blanket case-insensitivity would be dangerous. Spelled
// out there is no collision: nothing else is called `celsius` in any case. And the convention only
// runs this way for temperature; nobody writes "5.4 Newtons".
//
// Offsets are SAMPLED from mathjs rather than written out. `definition: '1 celsius'` silently
// produces a unit with no offset at all — 0 of it converts to 0 K instead of 273.15 — and
// Fahrenheit's offset is 459.67 * 5/9, which is exactly the sort of constant that goes in slightly
// wrong and stays wrong.
for (const [capitalized, lower] of [
  ['Celsius', 'celsius'], ['Fahrenheit', 'fahrenheit'],
  ['Kelvin', 'kelvin'], ['Rankine', 'rankine'],
] as const) {
  try {
    const at0 = mathUnit(0, lower).toNumber('K');
    const at1 = mathUnit(1, lower).toNumber('K');
    const scale = at1 - at0;
    // `offset` is expressed in the NEW unit's own scale, not in the base: mathjs computes the base
    // value as (value + offset) * scale. Passing the kelvin offset directly gives 255.372 * 5/9 =
    // 141.87 for Fahrenheit — wrong by 113 K, and invisible on Celsius, whose scale is 1.
    createUnit(capitalized, { definition: `${scale} K`, offset: at0 / scale });
  } catch {
    // Already defined, or the lowercase form is gone from a future mathjs — skip silently, exactly
    // as the custom-unit loop above does.
  }
}

// ─── Public helpers ───────────────────────────────────────────────────────────

/**
 * Symbol spellings, and the ASCII each becomes. Read forwards by `normalizeSymbols` to parse what a
 * teacher typed, and BACKWARDS by `equivalentUnitLabels` to offer the symbol form as an accepted
 * alternative when the canonical was written in ASCII.
 *
 * That second direction is not optional. Grading compares strings, so with `um` stored a student
 * typing `µm` matches neither the canonical nor any option unless the symbol form is materialised —
 * and `µm` is the spelling the picker itself inserts. The list must therefore stay in step with
 * UNIT_SYMBOLS in unit-field.tsx; they are the same set seen from two sides.
 */
/** Indexed by digit: `SUPERSCRIPT_DIGITS.indexOf('²')` is 2, and `[2]` is `²`. One constant for
 *  both directions — reading a pasted `m⁻²` and writing one back out as an offered spelling. */
const SUPERSCRIPT_DIGITS = '⁰¹²³⁴⁵⁶⁷⁸⁹';

/**
 * The astronomical bodies, and how their symbol is properly written.
 *
 * `Msun` is a spelling of convenience. Astronomy writes the body as a SUBSCRIPT — M⊙, R⊕, M♃ — and
 * a name is all mathjs can hold, so the notation exists only as a written form.
 *
 * A HOMOGLYPH is accepted but never offered. U+2609 SUN and U+2299 CIRCLED DOT OPERATOR are
 * different characters that draw the same mark, so rejecting one would trap anyone who pasted it
 * from a paper — and listing both puts two rows on screen that a reader cannot tell apart, which
 * reads as a bug rather than as a choice. Exactly how the two micro signs and the two omegas are
 * already handled: fold on the way in, offer one on the way out.
 *
 * U+2641 EARTH (♁) is NOT here, and that is the other half of the distinction: it draws a visibly
 * different mark, so it would be a real alternative rather than a homoglyph — and astronomy writes
 * M⊕, leaving ♁ to astrology. One symbol per body, and nothing accepted that is not shown.
 *
 * `MJ` is deliberately absent for Jupiter, common though it is in prose: it is already megajoules.
 */
const ASTRO_BODIES: Array<{ suffix: string; glyph: string; homoglyphs: string[] }> = [
  { suffix: 'sun',   glyph: '⊙', homoglyphs: ['☉'] },
  { suffix: 'earth', glyph: '⊕', homoglyphs: [] },
  { suffix: 'jup',   glyph: '♃', homoglyphs: [] },
];

/**
 * Spellings the PARSER can never hold, because mathjs allows only alphanumerics in a unit name.
 *
 * `light-year` is how the word is written in most prose and `createUnit` rejects the hyphen
 * outright; `M⊙` is how a solar mass is written everywhere it is written properly. Both can only
 * live here — rewritten on the way in, and offered back on the way out, exactly as the symbols are.
 *
 * The hyphen is NOT stripped generally, tempting as that is. `m-s` would become `ms`, which parses
 * as MILLISECONDS: a general rule would silently turn one unit into another, which is the same
 * hazard that keeps juxtaposition out of the compound generator. Each form is stated.
 */
const PUNCTUATED_FORMS: Array<{ written: string; parsed: string; offer?: boolean }> = [
  { written: 'light-years', parsed: 'lightyears' },
  { written: 'light-year',  parsed: 'lightyear'  },
  // Derived from the registry rather than listed, so a body added above cannot be missing here.
  ...CUSTOM_UNITS.flatMap(({ name }) => {
    const body = ASTRO_BODIES.find(b => name.length > b.suffix.length && name.endsWith(b.suffix));
    if (!body) return [];
    const quantity = name.slice(0, -body.suffix.length);
    // The GLYPH only. Spelling the body out in Unicode subscript letters — `Mₛᵤₙ` — was tried and
    // dropped: the Subscripts block has no `r`, `u` or `j`, so those three come from Phonetic
    // Extensions and Latin Extended-C instead, no UI font covers all three blocks, and the browser
    // falls back per CHARACTER. The result is a word whose letters sit at different heights and
    // weights. Not fixable by choosing other codepoints, because there are no others.
    return [
      { written: quantity + body.glyph, parsed: name },
      ...body.homoglyphs.map(glyph => ({ written: quantity + glyph, parsed: name, offer: false })),
    ];
  }),
];

/** For building a rule's backward regex from its literal ASCII form. */
const escapeForRegExp = (x: string) => x.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

const SYMBOL_TO_ASCII: Array<{
  /** Matched when PARSING what a teacher typed. */
  from: RegExp;
  /** The ASCII it becomes. */
  to: string;
  /** The symbol offered back when the canonical was written in ASCII. */
  symbol: string;
  /**
   * Where the ASCII may be turned BACK into the symbol, when a plain `split/join` would be too
   * eager. Only micro needs it: `u` is one letter that occurs inside plenty of unit names, and
   * `lux` -> `lµx` normalizes straight back to `lux`, so it passes every validity and equivalence
   * check and still has to be excluded. Restricted to a `u` that STARTS a unit token — beginning of
   * string or after an operator — and is followed by a letter, so `um` and `mol/uL` convert while
   * `lux` and a bare `u` do not.
   */
  backward?: { pattern: RegExp; replace: string };
}> = [
  { from: /°C/g, to: 'degC', symbol: '°C' },
  { from: /°F/g, to: 'degF', symbol: '°F' },
  { from: /°R/g, to: 'degR', symbol: '°R' },
  { from: /[\u2126\u03A9]/g, to: 'ohm', symbol: '\u03A9' },
  { from: /\u00C5/g, to: 'angstrom', symbol: '\u00C5' },
  { from: /\u207B\u00B9/g, to: '^-1', symbol: '\u207B\u00B9' },
  { from: /\u00B2/g, to: '^2', symbol: '\u00B2' },
  { from: /\u00B3/g, to: '^3', symbol: '\u00B3' },
  { from: /[\u00B7\u22C5\u00D7]/g, to: '*', symbol: '\u00B7' },
  { from: /\u2030/g, to: 'permille', symbol: '\u2030' },
  { from: /%/g, to: 'percent', symbol: '%' },
  { from: /[\u00B5\u03BC]/g, to: 'u', symbol: '\u00B5',
    backward: { pattern: /(^|[*/(^\s])u(?=[a-zA-Z])/g, replace: '$1\u00B5' } },
];

/**
 * The one spelling of each symbol, for a search to match against.
 *
 * Several of these have two codepoints that look identical: U+00B5 MICRO SIGN comes from a
 * keyboard and U+03BC GREEK SMALL MU from a maths editor; U+2126 OHM SIGN and U+03A9 GREEK CAPITAL
 * OMEGA likewise. The parser already accepts every one of them, so `μm` GRADES correctly — but the
 * index holds only one of each, so pasting the other found nothing and the field looked broken.
 *
 * Driven off the same table as `normalizeSymbols`, in the other direction, so a symbol added to one
 * cannot be missing from the other.
 */
function canonicalizeSymbols(label: string): string {
  return SYMBOL_TO_ASCII.reduce((acc, rule) => acc.replace(rule.from, rule.symbol), label);
}

/**
 * Normalize user-facing temperature symbols to the mathjs internal names.
 * mathjs requires unit names to start with an ASCII alpha character, so °C / °F
 * cannot be registered directly. We substitute them before every mathjs call so
 * teachers (and, later, students) can type the familiar degree-symbol form.
 */
function normalizeSymbols(label: string): string {
  // Driven by SYMBOL_TO_ASCII rather than a second chain of its own: the two directions must agree
  // on the same set, and a rule added to one and forgotten in the other is a symbol the picker
  // offers that the parser rejects, or an equivalent that never appears.
  //
  // Order is irrelevant — every rule matches characters no other rule matches.
  //
  // Superscript RUNS are handled ahead of the table, because the table can only map a fixed string
  // and a run carries its own digits: `s⁻²` is `^-2`, not anything `⁻¹`, `²` or `³` can spell. Those
  // three entries stay, since the table is also read backwards to offer the symbol form, and this
  // pass simply leaves nothing for them to match. Without it a squared denominator — `W·m⁻²`, as
  // ordinary in physics as `m/s` — failed to parse and so was never offered as a spelling.
  // Ahead of everything else: these are whole words, and the rules below work on characters.
  const depunctuated = PUNCTUATED_FORMS.reduce(
    (acc, f) => acc.replace(new RegExp(escapeForRegExp(f.written) + '(?![A-Za-z])', 'g'), f.parsed),
    label);
  const desuperscripted = depunctuated.replace(/[⁻⁰¹²³⁴-⁹]+/g, run =>
    '^' + [...run].map(ch => ch === '⁻' ? '-' : String(SUPERSCRIPT_DIGITS.indexOf(ch))).join(''));
  const substituted = SYMBOL_TO_ASCII.reduce((acc, rule) => acc.replace(rule.from, rule.to), desuperscripted);
  // Not in the table because nothing offers it back: `-` is on the keyboard, so there is no symbol
  // form to suggest. But Word, Google Docs, PDFs and MathLive all substitute U+2212 for a typed
  // hyphen in mathematical text, so `s^−1` arrives by paste looking identical to `s^-1`.
  return substituted.replace(/\u2212/g, '-');
}


/**
 * Is this a temperature, and therefore a unit whose conversions may carry an offset?
 *
 * Asked of mathjs by DIMENSION rather than by name. This was a four-name set — `K`, `degC`, `degF`,
 * `degR` — which meant a teacher who typed `celsius`, `fahrenheit`, `rankine` or `kelvin` got no
 * "Interpret as" control at all, despite those being the same units spelled out and equally subject
 * to the absolute-versus-interval question. Any spelling mathjs accepts now answers correctly, and
 * a future temperature scale needs no edit here.
 */
export function isOffsetTempUnit(label: string): boolean {
  const trimmed = label.trim();
  if (!trimmed) return false;
  try {
    return mathUnit(1, normalizeSymbols(trimmed)).equalBase(mathUnit(1, 'K'));
  } catch {
    return false;
  }
}

/** Returns true if the string is a mathjs-recognized unit (including custom extensions). */
export function isValidUnit(label: string): boolean {
  if (!label.trim()) return false;
  // Pure-numeric strings (e.g. "123") are accepted by mathjs as dimensionless quantities but are
  // not meaningful units — reject them up front.
  //
  // Tested AFTER normalization, not before. A unit written entirely in symbols has no ASCII letter
  // of its own: `Ω`, `Å`, `‰` and `%` were all rejected by this guard before the substitution that
  // turns them into `ohm`, `angstrom`, `permille` and `percent` ever ran. Normalizing first keeps
  // the guard doing its actual job — `123` still has no letters once normalized, and still fails.
  const normalized = normalizeSymbols(label);
  if (!/[a-zA-Z]/.test(normalized)) return false;
  // A bare micro sign is a PREFIX, not a unit, and the substitution above hides that: `µ` becomes
  // `u`, which is the unified atomic mass unit. So `µ` on its own parsed, and a student who typed
  // the symbol and stopped was silently answering in daltons. Nothing else in the table collides
  // this way — `m` really is both a prefix and the metre — so the guard names the one case.
  if (/^[\u00B5\u03BC]$/.test(label.trim())) return false;
  try { mathUnit(normalized); return true; } catch { return false; }
}

/**
 * Returns { factor, offset } to convert a value from canonicalUnit to targetUnit via:
 *   convertedValue = canonicalValue × factor + offset
 *
 * For linear units (m → km, m/s → mph, …) offset is always 0.
 * For offset temperature scales (°C ↔ °F ↔ K) the offset carries the intercept:
 *   - absolute mode  (temperature readings): uses the full affine formula so that
 *     0°C → 32°F, 100°C → 212°F, etc.
 *   - interval mode  (temperature differences): offset is forced to 0 so that
 *     1°C → 1.8°F, 5°C → 9°F, etc.
 *
 * Returns null if the units are dimensionally incompatible.
 */
export function getConversionFactor(
  canonicalUnit: string,
  targetUnit: string,
  tempMode: 'absolute' | 'interval' = 'absolute',
): { factor: number; offset: number } | null {
  try {
    const nc = normalizeSymbols(canonicalUnit);
    const nt = normalizeSymbols(targetUnit);
    // Derive factor and offset from mathjs by sampling at 0 and 1.
    // For linear units: at0=0, factor=at1. For temperature: at0=32 (°C→°F), factor=1.8.
    const at0 = mathUnit(0, nc).toNumber(nt);
    const at1 = mathUnit(1, nc).toNumber(nt);
    const factor = at1 - at0;
    const offset = tempMode === 'absolute' ? at0 : 0;
    if (!Number.isFinite(factor) || !Number.isFinite(offset)) return null;
    return { factor, offset };
  } catch {
    return null;
  }
}

/**
 * Every other spelling of the same SIMPLE unit — `g` gives `gram, grams, gramme, grammes`.
 * Half of `acceptedSpellings`; a compound unit has no table entry and is generated instead.
 *
 * "Same" is a conversion of exactly factor 1 and offset 0, which is stricter than "convertible" and
 * is the distinction the feature turns on. `centimetre` converts to `m` at factor 0.01, so it is NOT
 * an equivalent: answering in the wrong scale is a real mistake, and stays one the teacher may
 * deliberately mark wrong. Only pure spelling variance collapses. Temperature behaves too — `degC`
 * yields `celsius`, while `K` carries a 273.15 offset and is correctly excluded.
 *
 * SHARED BASE is required as well as factor 1, and that second test is not redundant. A kilogram and
 * a millitonne are exactly equal, as are a litre and a cubic decimetre, so factor alone pulls in
 * `mt`, `millitonne`, `microtonne`, `dm3` — true, unwritten by anyone, and reading like a bug in a
 * teacher-facing list. An alternative SPELLING is the same unit written differently, not a different
 * unit that happens to coincide.
 *
 * Enumerated from mathjs's own table, which is the set `isValidUnit` already holds the teacher to,
 * so nothing can be accepted here that the teacher could not have authored.
 */
/**
 * Every table name that IS the given base unit, keyed by base name.
 *
 * Built once, in one pass, because the alternative is what it replaced: a unit conversion against
 * all 318 entries for every unit whose spellings are wanted. Signature equality decides membership
 * — same dimensions, same scale, same offset, same family — which is the same question the
 * conversion was asking and is also how `acceptedSpellings` proves its final answer.
 *
 * NOT a bare signature grouping: `kg` and `millitonne` share a signature exactly, and are not
 * spellings of each other. Grouping by the BASE's signature is what keeps them apart, since `g` and
 * `tonne` do not share one.
 */
let _sameBase: Map<string, string[]> | null = null;
function sameBaseNames(base: string): string[] {
  if (!_sameBase) {
    _sameBase = new Map();
    const byKey = new Map<string, string[]>();
    const keyOf = new Map<string, string>();
    for (const name of Object.keys(Unit.UNITS)) {
      const signature = unitSignature(name);
      if (!signature) continue;
      const key = `${signature.dims}|${signature.factor}|${signature.offset}|${signature.family ?? ''}`;
      keyOf.set(name, key);
      const bucket = byKey.get(key);
      if (bucket) bucket.push(name); else byKey.set(key, [name]);
    }
    for (const [name, key] of keyOf) _sameBase.set(name, byKey.get(key) ?? [name]);
  }
  return _sameBase.get(base) ?? [base];
}

const _equivalentsCache = new Map<string, string[]>();
function enumerateSimpleSpellings(canonicalUnit: string): string[] {
  // ALWAYS absolute, never the block's tempConvMode. Whether two names denote the same unit is a
  // fact about the units; how a teacher is interpreting a reading is not. In interval mode the
  // offset is dropped, so `degC -> K` becomes factor 1 / offset 0 and kelvin would be auto-added as
  // another SPELLING of celsius — locked to correct, in the "Also accepted" line, ungrindable by
  // the teacher. A temperature DIFFERENCE of 5 °C and 5 K are indeed the same size, but °C and K
  // are not the same unit, and whether to accept both is the teacher's call rather than ours.
  // Both the value test and the base test would be fooled together, since both run in this mode.
  const tempMode = 'absolute' as const;
  const canon = canonicalUnit.trim();
  if (!canon || !isValidUnit(canon)) return [];
  const key = canon;
  const hit = _equivalentsCache.get(key);
  if (hit) return hit;

  const normalized = normalizeSymbols(canon);
  const equal = (a: string, b: string) => {
    const conv = getConversionFactor(a, b, tempMode);
    // Exact in principle; an epsilon because these arrive through floating-point sampling.
    return !!conv && Math.abs(conv.factor - 1) < 1e-12 && Math.abs(conv.offset) < 1e-12;
  };

  let out: string[] = [];
  try {
    // The canonical's own base and prefix: `kg` is `k` + `g`, and `µm` is `micro` + `m`.
    const parsed = mathUnit(normalized).units[0];
    const base = parsed?.unit.name;
    // mathjs types this as a string; at runtime it is `{ name, value, scientific }`, and the empty
    // prefix is an object with `name: ''` rather than absent.
    const prefixValue = (parsed?.prefix as unknown as { value: number } | undefined)?.value ?? 1;
    if (base) {
      // The other names for that base, looked UP rather than scanned for. Scanning meant a unit
      // conversion against all 318 table entries, and then up to 25 more per survivor to find the
      // prefixed form — 10ms per unit, and 800ms to build the suggestion index out of them.
      for (const name of sameBaseNames(base)) {
        // Only the RAW canonical is excluded, not its normalized spelling. They are different
        // strings, and grading compares strings: with `°C` stored, `degC` is a DIFFERENT thing a
        // student may type, so it has to be offered as an equivalent rather than skipped as
        // "self". Same for `µm`, whose normalized twin is `um`.
        if (name !== canon && prefixValue === 1 && equal(canon, name)) out.push(name);
        if (prefixValue === 1) continue;
        // The canonical carries a prefix, so each alternative name needs the SAME MAGNITUDE from
        // its own prefix table — `g` takes `k`, `gram` takes `kilo`, and they are the same
        // thousand. Found by value rather than by trying all 25, which is what made this quadratic.
        const units = Unit.UNITS as Record<string, { prefixes?: Record<string, { value: number }> }>;
        for (const [prefixName, prefix] of Object.entries(units[name]?.prefixes ?? {})) {
          if (prefix.value !== prefixValue) continue;
          const form = prefixName + name;
          if (form !== canon && equal(canon, form)) out.push(form);
        }
      }
    }
  } catch {
    out = [];
  }
  // Spellings enumeration can never find, because `Unit.UNITS` holds ASCII identifiers while these
  // are either INPUTS that normalize into one (`‰`, `µm`) or COMPOUNDS composed at parse time
  // (`m^2`). Grading compares strings, so a spelling that is not materialised is a spelling that
  // does not count.
  //
  // Generated by transform rather than by loosening the shared-base filter. That filter is what
  // keeps `millitonne` out of `kg` — equal in value, different unit — and `m2` would come back in
  // with it: `baseOf('m^2')` is `m` while `baseOf('m2')` is `m2`, so they look as unrelated to that
  // test as the tonne does. Generating the candidate and then PROVING it is the same unit keeps
  // both answers right.
  //
  // Applied twice so transforms compose: `m2` -> `m^2` on the first pass, `m^2` -> `m²` on the
  // second. Everything is validated before it is offered, so a transform that produces nonsense
  // for some input simply yields nothing.
  // `(?![A-Za-z])` is what stops a rule firing on a word it merely STARTS: `angstroms` was being
  // offered as `Ås`, and `ohms` as `Ωs`, because a plain substring swap cannot see that the word
  // continues. The symbol stands for the whole unit name, never for a fragment of a longer one.
  const toSymbols = (x: string) => SYMBOL_TO_ASCII.reduce(
    (acc, rule) => rule.backward
      ? acc.replace(rule.backward.pattern, rule.backward.replace)
      : acc.replace(new RegExp(escapeForRegExp(rule.to) + '(?![A-Za-z])', 'g'), rule.symbol),
    x);
  /**
   * The same trip back: `lightyear` is what parses, `light-year` is what people write.
   *
   * Returns ALL of them, rather than folding them together. A unit can have several written forms —
   * `Msun` is `M⊙`, `M☉` and `Mₛᵤₙ` — and a chained replace would produce only the first, since
   * after it there is no `Msun` left for the next rule to find.
   */
  const toPunctuated = (x: string) => PUNCTUATED_FORMS.flatMap(f => {
    if (f.offer === false) return [];
    const pattern = new RegExp(escapeForRegExp(f.parsed) + '(?![A-Za-z])', 'g');
    return pattern.test(x) ? [x.replace(pattern, f.written)] : [];
  });
  /**
   * `m^2` -> `m2`, and `W/m^2` -> `W/m2`. Per TOKEN, not anchored to the whole string: these were
   * whole-string patterns, so they fired on a bare `m^2` and never inside a compound — which is
   * most of where they matter, since `W/m^2`, `kg/m^3` and `cd/m^2` are how derived units are
   * actually written. mathjs has the compact form for some names and not others (`m2`, `cm3`, `km2`
   * exist; `ft2`, `s2` do not), so validation decides; this just proposes.
   *
   * `\d` and not `-?\d`: `s^-1` must not become `s-1`, which is not a unit and not intended.
   */
  const compactPower = (x: string) => x.replace(/([a-zA-Z]+)\^(\d)/g, '$1$2');
  /** The inverse, so a canonical authored compactly finds the others: `W/m2` -> `W/m^2`. */
  const expandPower = (x: string) => x.replace(/([a-zA-Z]+)(\d)/g, '$1^$2');
  /**
   * The imperial area and volume names, which spell their own exponent: `sqft` <-> `ft^2`,
   * `cuin` <-> `in^3`. Both directions, so it does not matter which the teacher reached for.
   *
   * These cannot come from the table loop however it is widened. `sqft` is its own entry in mathjs
   * with its own base, so it looks as unrelated to `ft` as a tonne does to a kilogram — and a
   * student writing `ft^2` for a canonical of `sqft` was marked wrong for a notation choice.
   * Proposed here and proven like everything else, so `sqm` and `cucm`, which are not units,
   * simply never appear.
   */
  const spelledExponents = (x: string) => [
    x.replace(/\bsq([a-zA-Z]+)/g, '$1^2'), x.replace(/\bcu([a-zA-Z]+)/g, '$1^3'),
    x.replace(/\b([a-zA-Z]+)\^2/g, 'sq$1'), x.replace(/\b([a-zA-Z]+)\^3/g, 'cu$1'),
    // `cc` is the one that spells no exponent at all, and it is the commonest of the set in a
    // school lab. Nothing generalises it, so it is stated.
    x === 'cc' ? 'cm^3' : x === 'cm^3' ? 'cc' : x,
  ];

  // Seeded with the TABLE names as well as the canonical, so a transform can start from any of
  // them. `celsius` has no `degC` inside it for the symbol rule to rewrite, so the symbol form was
  // reachable from `degC` and from nowhere else — a teacher whose canonical was the WORD silently
  // rejected the `°C` that the block's own symbol picker inserts.
  const spellings = new Set([canon, ...out]);
  for (let pass = 0; pass < 2; pass++) {
    for (const form of [...spellings]) {
      for (const t of [toSymbols, normalizeSymbols, compactPower, expandPower]) {
        const candidate = t(form);
        if (candidate !== form) spellings.add(candidate);
      }
      for (const candidate of [...spelledExponents(form), ...toPunctuated(form)]) {
        if (candidate !== form) spellings.add(candidate);
      }
    }
  }
  for (const candidate of spellings) {
    if (candidate !== canon && isValidUnit(candidate) && equal(canon, candidate)) out.push(candidate);
  }

    out = [...new Set(out)];
  out.sort((a, b) => a.length - b.length || a.localeCompare(b));
  _equivalentsCache.set(key, out);
  return out;
}

// ─── Unit identity ───────────────────────────────────────
// The SHAPE and the COMPARISON live in @agavi/blocks, because evaluation needs them and must stay
// free of a unit parser. COMPUTING a signature is here, because it needs mathjs plus everything
// this file registers: the international spellings, the symbol substitutions, the custom units.

/**
 * Units that share a signature and are NOT the same thing.
 *
 * Deliberately tiny, and the only place in the unit layer where a judgement is encoded rather than
 * derived. Keyed by mathjs's base unit name, so a prefixed form resolves too: `mGy` is `Gy`.
 *
 * `J` and `N*m` are absent on purpose. They share a signature, but one is a named unit and the
 * other is a composition of it, and a student answering an energy question in N·m has given the
 * right answer in longhand. Only NAMED-versus-NAMED needs separating, which is why `family` is
 * compared only when both sides carry one.
 */
const COLLISION_FAMILY: Record<string, string> = {
  Gy: 'absorbed-dose',   gray: 'absorbed-dose',      grays: 'absorbed-dose',
  // Real power against apparent power. `VA` is dimensionally a watt and deliberately not one: an
  // AC circuit delivering 100 VA at a lagging power factor is not delivering 100 W, which is the
  // whole reason the volt-ampere has its own name. Both sides need a family, because the tag only
  // separates when BOTH carry one — that is what leaves `W` matching `J/s`, a composition of it.
  W: 'power',            watt: 'power',              watts: 'power',
  VA: 'apparent-power',
  Sv: 'equivalent-dose', sievert: 'equivalent-dose',  sieverts: 'equivalent-dose',
  Hz: 'frequency',       hertz: 'frequency',
  // EVERY alias, including the plural. mathjs registers an alias as a unit name in its own right,
  // so a component resolves to `becquerels` rather than to `Bq`, and a family listing only the
  // singular leaves the plural family-less — which reads as "no collision" and let `becquerels` be
  // offered as a spelling of `Hz`.
  Bq: 'activity',        becquerel: 'activity',       becquerels: 'activity',
};

export function unitSignature(label: string): UnitSignature | null {
  const trimmed = label.trim();
  if (!trimmed || !isValidUnit(trimmed)) return null;
  try {
    const normalized = normalizeSymbols(trimmed);
    const at0 = mathUnit(0, normalized).toSI().toNumber();
    const at1 = mathUnit(1, normalized).toSI().toNumber();
    if (!Number.isFinite(at0) || !Number.isFinite(at1)) return null;
    const parsed = mathUnit(1, normalized);
    // A family belongs to a NAMED unit, so only a single component raised to the first power can
    // carry one. `J/kg` is a composition and takes none, which is what lets it equal both Gy and Sv
    // while they stay distinct from each other.
    const single = parsed.units.length === 1 && parsed.units[0]?.power === 1
      ? parsed.units[0]?.unit.name : undefined;
    const family = single ? COLLISION_FAMILY[single] : undefined;
    return {
      dims: parsed.dimensions.join(','),
      factor: at1 - at0,
      offset: at0,
      ...(family ? { family } : {}),
    };
  } catch {
    return null;
  }
}

// ─── Accepted spellings ───────────────────────────────────

/**
 * Every way this unit may be written, as a list a teacher can see.
 *
 * Identity alone was enough to GRADE — two units with the same signature are the same unit — but it
 * decided silently and all at once. A teacher could not see what was being accepted, and could not
 * accept `joule` while rejecting some other spelling. An enumerated set restores both: it is shown,
 * and any member can be broken out and re-flagged.
 *
 * Two sources, one rule. SIMPLE units are enumerated from mathjs's table; COMPOUND ones are
 * generated from their own components, since no table contains them. Every candidate from either
 * source is then PROVEN with a signature before it is offered, which is what keeps a generated
 * spelling from being a different unit that merely looks related.
 *
 * The canonical itself is never included. The list reads as "also accepted", and a canonical listed
 * there could be broken out into an option duplicating the canonical — which the duplicate check
 * would then correctly flag as an error the teacher did not create.
 */
const _spellingsCache = new Map<string, string[]>();
export function acceptedSpellings(canonicalUnit: string): string[] {
  const canon = canonicalUnit.trim();
  if (!canon) return [];
  // Cached because the block re-derives its data on render, and this proves every candidate by
  // parsing it — a dozen mathjs calls per unit, on every keystroke elsewhere in the editor. The
  // registry is fixed after module load, so the answer for a given string never changes.
  const cached = _spellingsCache.get(canon);
  if (cached) return cached;
  const own = unitSignature(canon);
  if (!own) return [];
  const proven = (candidate: string) =>
    candidate !== canon && sameUnitSignature(own, unitSignature(candidate));

  const out = new Set<string>();
  for (const c of enumerateSimpleSpellings(canon)) if (proven(c)) out.add(c);
  for (const c of generateCompoundSpellings(canon)) if (proven(c)) out.add(c);
  // Shortest first, then alphabetical, so the line reads `gram, grams, gramme, grammes`.
  const sorted = [...out].sort((a, b) => a.length - b.length || a.localeCompare(b));
  _spellingsCache.set(canon, sorted);
  return sorted;
}

/**
 * Notation variants of a COMPOUND, built from its own components.
 *
 * mathjs hands back each component already as prefix-plus-SYMBOL — `mg`, not `milligram` — so
 * `mg/liter` and `newton*meter` are never produced. That is the line: a compound unit is written in
 * symbols, and spelling one out longhand is more work than the notation it replaces.
 *
 * JUXTAPOSITION IS EXCLUDED, deliberately. `Nm` does not parse at all, and where juxtaposition does
 * parse it means something else: `ms⁻¹` is inverse MILLISECONDS, a thousand times `m/s`. Nothing
 * structural separates the safe case from the dangerous one — both are two symbols pushed together
 * — so the whole shape stays out rather than relying on the proof step to catch the bad half.
 */
function generateCompoundSpellings(canonicalUnit: string): string[] {
  let parts: { sym: string; pow: number }[];
  try {
    parts = mathUnit(1, normalizeSymbols(canonicalUnit)).units.map(u => ({
      // mathjs's own types declare `prefix: string`; at runtime it is `{ name, value, scientific }`
      // and the empty prefix is an object with `name: ''`, not absent. Reading it as the string the
      // types promise would silently prepend `[object Object]` to every prefixed symbol.
      sym: ((u.prefix as unknown as { name: string } | undefined)?.name ?? '') + u.unit.name,
      pow: u.power,
    }));
  } catch {
    return [];
  }
  if (parts.length < 2) return [];

  const numerator = parts.filter(p => p.pow > 0);
  const denominator = parts.filter(p => p.pow < 0);
  if (numerator.length === 0) return [];

  const out = new Set<string>();
  for (const sep of ['*', '·']) {
    for (const superscriptPowers of [false, true]) {
      const superscript = (n: number) =>
        String(n).split('').map(d => SUPERSCRIPT_DIGITS[Number(d)]).join('');
      const power = (n: number) => n === 1 ? '' : superscriptPowers ? superscript(n) : `^${n}`;
      const top = numerator.map(p => p.sym + power(p.pow)).join(sep);
      if (denominator.length === 0) { out.add(top); continue; }
      // Both ways of saying "per": a solidus, and a negative exponent.
      out.add(`${top}/${denominator.map(p => p.sym + power(-p.pow)).join(sep)}`);
      const negative = (n: number) => superscriptPowers ? '⁻' + superscript(n) : `^-${n}`;
      out.add([top, ...denominator.map(p => p.sym + negative(-p.pow))].join(sep));
    }
  }
  return [...out];
}

// ─── The suggestion index ─────────────────────────────────

/** One selectable row: a single SPELLING, not a unit. */
export interface UnitSuggestion {
  /** Exactly what goes into the field when this row is chosen. */
  label: string;
  /** The shortest spelling of the same unit, shown beside the row when it differs — so a student
   *  picking `grams` can see it is the same thing as `g` without being moved onto it. */
  primary: string;
}

// The row deliberately carries NOTHING ELSE. A quantity beside each one ("Mass", "Volume") reads as
// a help text and is really a hint: the units a student is offered while typing would then tell
// them what kind of quantity the answer is, which is part of what the question asks.

/**
 * The units a school lab actually reaches for, in the order a list should offer them.
 *
 * Without this, typing `m` offers `mmHg` and `mil` above `meter` — every ranking that can be
 * derived from the string alone (length, alphabetical, prefix match) treats them as equals, and
 * they are not. Prefixed forms are here for the same reason: `cm` and `mL` are not index entries of
 * their own, since enumerating 24 prefixes across 133 units is not a list anyone can read.
 */
const COMMON_UNITS = [
  'm', 'cm', 'mm', 'km', 'µm', 'nm', 'g', 'kg', 'mg', 'µg', 's', 'ms', 'min', 'h', 'day',
  'L', 'mL', 'µL', 'mol', 'mmol', 'µmol', 'M', 'N', 'J', 'kJ', 'W', 'kW', 'Pa', 'kPa',
  '°C', 'K', 'Hz', 'kHz', 'V', 'mV', 'A', 'mA', 'Ω', 'C', 'lx', '%', 'ppm', 'ppb', '‰',
  'deg', 'rad', 'm/s', 'm/s^2', 'g/mL', 'mg/L', 'mol/L', 'W/m^2', 'kg/m^3',
];

/**
 * The bases worth offering a prefix on, in the order a list should offer them.
 *
 * Used only to COMPLETE a prefix someone is part-way through typing. Enumerating 24 prefixes across
 * every unit is thousands of rows and no ranking would survive it; this is the handful that anyone
 * actually prefixes, and everything else is still reached by typing it out, since the field
 * validates by parsing rather than by membership.
 */
const PREFIXABLE_BASES = ['m', 'g', 's', 'L', 'mol', 'N', 'J', 'W', 'Pa', 'V', 'A', 'Ω', 'Hz', 'K',
                          'C', 'lx', 'Bq', 'Sv', 'Gy'];

/** Every SI prefix, read off the registry rather than listed, so the two cannot disagree. */
const SI_PREFIXES = new Set(
  Object.keys((Unit.UNITS as Record<string, { prefixes?: Record<string, unknown> }>).m?.prefixes ?? {})
    .filter(Boolean));

/** One row, however it was arrived at — from the table or generated on the spot. */
function suggestionFor(label: string): UnitSuggestion {
  const group = [label, ...acceptedSpellings(label)];
  // Shortest wins. On a tie the SYMBOL does — `µm` over `um`, since the symbol is the form the
  // unit is properly written in and the one the picker inserts — and after that the capital,
  // because an equal-length ASCII pair differs only in case (`L` against `l`) and a lowercase l
  // reads as a 1.
  const symbolic = (x: string) => (/[^\u0020-\u007E]/.test(x) ? 1 : 0);
  const upper = (x: string) => [...x].filter(c => c !== c.toLowerCase()).length;
  const primary = [...group].sort((a, b) =>
    a.length - b.length || symbolic(b) - symbolic(a) || upper(b) - upper(a) || a.localeCompare(b))[0]!;
  return { label, primary };
}

let _index: UnitSuggestion[] | null = null;

/**
 * Every spelling of every unit, as selectable rows.
 *
 * ROWS ARE SPELLINGS, not units. A row per unit would mean choosing one had to rewrite what the
 * student typed — `grams` silently becoming `g` — and that rewriting is exactly what a teacher who
 * broke `grams` out into a row of its own was trying to distinguish. One row per spelling keeps
 * what is chosen and what is graded the same string.
 *
 * PREFIXED FORMS are here only for the common units above. The full cross-product is thousands of
 * entries, so anything rarer is reached by typing it out: the field validates by PARSING, and the
 * list only has to make the common path fast.
 */
export function unitIndex(): UnitSuggestion[] {
  if (_index) return _index;
  const seen = new Set<string>();
  const out: UnitSuggestion[] = [];
  const add = (label: string) => {
    if (seen.has(label) || !isValidUnit(label)) return;
    // A COMPOUND contributes only itself. Its spellings are notation variants — `mol*L⁻¹`,
    // `mol·L^-1` — and a list of those is not a choice anyone is making; the compound is composed
    // token by token instead, which is what `unitTokenAt` is for.
    const compound = /[*/·×^]/.test(label);
    const group = compound ? [label] : [label, ...acceptedSpellings(label)];
    const { primary } = suggestionFor(label);
    for (const spelling of group) {
      if (seen.has(spelling)) continue;
      seen.add(spelling);
      out.push({ label: spelling, primary });
    }
  };
  for (const label of COMMON_UNITS) add(label);
  for (const name of Object.keys(Unit.UNITS)) add(name);
  _index = out;
  return out;
}

/**
 * The rows to offer for what has been typed so far.
 *
 * Case is load-bearing in units — `K` is kelvin and `k` is kilo, `mS` is millisiemens and `ms` a
 * millisecond — so an exact-case match ranks above a loose one. But the search itself stays
 * case-INSENSITIVE, because a student who does not yet know that is precisely the one who needs the
 * list; they are shown the correct casing and choose it.
 *
 * Nothing is offered for an empty token: see below.
 */
export function suggestUnits(token: string, limit = 8): UnitSuggestion[] {
  const index = unitIndex();
  const raw = canonicalizeSymbols(token.trim());
  // Nothing at all until something is typed. An unprompted list of common units is a menu of what
  // the answer might be, and offering it the moment the box is focused answers half the question
  // before the student has thought about it.
  if (!raw) return [];
  // An exponent is not a unit. `W/m^2` puts the caret in a token of `2`, and offering units there
  // would be answering a question nobody asked.
  if (/^\d+$/.test(raw)) return [];
  const lower = raw.toLowerCase();

  /**
   * Two rows the table cannot hold, generated for this query alone.
   *
   * WHAT WAS TYPED, whenever it parses. The index holds a curated set of prefixed forms, so `Qm` is
   * a perfectly good unit with no row — and without one the list said "Unit not found" about
   * something the field accepts, and Enter, which takes the highlighted row, REPLACED it with
   * whatever loosely matched (`sqmi` contains `qm`). What you typed is always the first option, so
   * Enter on it is a no-op.
   *
   * A PREFIX PART-WAY THROUGH. `Q` is not a unit and never will be, but it is not nonsense either,
   * and answering it with "Unit not found" is wrong about what is happening. Completing it against
   * the bases people actually prefix turns the whole SI table into something you can discover by
   * typing, without enumerating 24 prefixes across 133 units.
   */
  const generated: UnitSuggestion[] = [];
  if (isValidUnit(raw) && !index.some(e => e.label === raw)) generated.push(suggestionFor(raw));
  if (SI_PREFIXES.has(normalizeSymbols(raw))) {
    for (const base of PREFIXABLE_BASES) {
      const candidate = raw + base;
      if (isValidUnit(candidate) && !index.some(e => e.label === candidate)) {
        generated.push(suggestionFor(candidate));
      }
    }
  }

  const tier = (label: string) => {
    if (label === raw) return 0;
    if (label.startsWith(raw)) return 1;
    const l = label.toLowerCase();
    if (l === lower) return 2;
    if (l.startsWith(lower)) return 3;
    // Substring matching needs two characters to mean anything: on one, `x` would offer `lx` and
    // `lux`, and a student typing a letter that is not a unit should be told so, not handed a
    // coincidence. A single SYMBOL is exempt — `⊙` occurs in nothing by accident, and it is the
    // half of `M⊙` someone is most likely to have on the clipboard.
    if ((raw.length > 1 || !/^[a-zA-Z]$/.test(raw)) && l.includes(lower)) return 4;
    return 5;
  };
  const ranked = [...index, ...generated]
    .map((entry, order) => ({ entry, order, tier: tier(entry.label) }))
    .filter(x => x.tier < 5)
    // `order` carries the curated ranking, so within a tier the school units come first and
    // everything else keeps registry order. Length breaks the remaining ties toward the shorter
    // spelling, which is the one a student is more likely to have meant.
    .sort((a, b) => a.tier - b.tier || a.order - b.order || a.entry.label.length - b.entry.label.length)
    .map(x => x.entry);

  // Round-robin by unit, so the first spelling of every unit comes before the second of any.
  // Straight ranking gave `k` five ways to write kilometre and then kilogram: eight rows, two
  // units. Spreading them turns the same eight rows into km, kg, kJ, kPa, kW and their alternates,
  // which is the difference between a list that answers "which spelling" and one that answers
  // "which unit" — and on an eight-row cap, the second question is nearly always the live one.
  const byUnit = new Map<string, UnitSuggestion[]>();
  for (const entry of ranked) {
    const bucket = byUnit.get(entry.primary);
    if (bucket) bucket.push(entry); else byUnit.set(entry.primary, [entry]);
  }
  const out: UnitSuggestion[] = [];
  for (let round = 0; out.length < limit; round++) {
    const before = out.length;
    for (const bucket of byUnit.values()) {
      const entry = bucket[round];
      if (entry) out.push(entry);
      if (out.length === limit) return out;
    }
    if (out.length === before) break;
  }
  return out;
}

/**
 * The stretch of `text` around `caret` that names ONE unit, as [start, end).
 *
 * A compound is several units in one string, so suggesting against the whole string would offer
 * nothing once `mg/` has been typed. Splitting on the operators means the list keeps working
 * through `mg` then `L`, which is what makes a compound composable from the list rather than only
 * typeable from memory.
 */
export function unitTokenAt(text: string, caret: number): [number, number] {
  const isBoundary = (ch: string) => /[*/·×^()\s+-]/.test(ch);
  let start = Math.min(caret, text.length);
  while (start > 0 && !isBoundary(text[start - 1]!)) start--;
  let end = caret;
  while (end < text.length && !isBoundary(text[end]!)) end++;
  return [start, end];
}
